import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Avatar from 'components/field/Avatar';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import { FormFooter } from 'components/form/FormLayout.styled';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import FormHeader from 'components/global/FormHeader';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import { usePrompt } from 'hooks/usePrompt';

import OTPVerification from 'pages/OTP/OTPVerification';
import * as S from 'pages/Settings/Profile/ProfileSettings.styled';

import { PROFILE } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { faBadgeCheck, faBrush, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import { GeneralSettingsContainer } from '../General/GeneralSettings.styled';
import LayoutPreferenceModal from './LayoutPreferenceModal';

const UserSettings = () => {
	const { Country } = useLocale();
	const user = useSelector((state) => state?.user);
	const [isLoading, setIsLoading] = useState(false);
	const [isEditMode, setEditMode] = useState(false);

	const axios = useAxiosPrivate();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { update } = useAppTracking();

	const { showSuccess, showError, showWarning } = useAlert();
	const isTrUser = useMemo(() => Country === 'TR' && user.planName !== 'EnterprisePackage', [Country]);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					avatarPath: yup.string().nullable(),
					fullName: yup.string().nullable(),
					firstName: yup.string().nullable().required(),
					lastName: yup.string().nullable().required(),
					idNumber: yup
						.string()
						.nullable()
						.when([], {
							is: () => isTrUser,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					birthdate: yup
						.string()
						.nullable()
						.when([], {
							is: () => isTrUser,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					phone: yup.string().nullable().phoneNumber(),
					email: yup.string().email().required()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			fullName: null,
			firstName: null,
			lastName: null,
			idNumber: null,
			birthdate: null,
			phone: null,
			email: null,
			avatarPath: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, dirtyFields }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const loadForm = () => {
		const {
			fullName = null,
			firstName = null,
			lastName = null,
			phone = null,
			email = null,
			avatarPath = null,
			dateOfBirth = null,
			nationalID = null
		} = user;
		setIsLoading(true);
		try {
			reset({
				fullName,
				firstName,
				lastName,
				phone,
				email,
				birthdate: dateOfBirth,
				idNumber: nationalID,
				avatarPath
			});
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: `Error`,
				message: `Error while getting user details`
			});
		}
		setIsLoading(false);
	};

	const onSubmit = useCallback(
		async ({ ID, avatarPath, fullName, firstName, lastName, email, idNumber, birthdate }) => {
			setIsLoading(true);
			try {
				const response = await axios.post(PROFILE.UPDATE_USER_DETAILS, {
					ID,
					avatarPath,
					fullName,
					firstName,
					lastName,
					email,
					...(birthdate && { dateOfBirth: birthdate }),
					...(idNumber && { identityNo: +idNumber })
				});
				const { status } = response;

				if (status === 200) {
					if (dirtyFields?.email) {
						showWarning({
							message: 'emailChange.loginAgain'
						});
						showSuccess();
						setTimeout(() => {
							navigate(PATHS.LOGOUT);
						}, 2000);
						return;
					}
					dispatch(
						setUserDetails({
							avatarPath,
							fullName: `${firstName} ${lastName}`,
							firstName,
							lastName,
							email,
							dateOfBirth: birthdate,
							nationalID: idNumber,
							nationalIDVerified: false
						})
					);
					showSuccess();
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(`Error while updating user details`, JSON.stringify(error));
				if (error?.response?.data?.code === 'error.EM01') {
					showError({
						header: 'settingsUpdateFailed',
						message: error?.response?.data?.code
					});
					return;
				}
				showError({
					header: 'settingsUpdateFailed',
					message: error?.response?.data?.code
				});
			} finally {
				setIsLoading(false);
			}
		},
		[dirtyFields]
	);

	useEffect(() => {
		if (!user) return;
		loadForm();
	}, [user]);

	useEffect(() => {
		if (isTrUser && user.nationalIDVerified) {
			setEditMode(false);
		}
		setEditMode(!(isTrUser && user.nationalIDVerified));
	}, [user?.nationalIDVerified, isTrUser]);

	const openEditProfileAlert = () => {
		NiceModal.show(ConfirmationModal, {
			title: 'idVerification',
			subTitle: 'youHaveToVerifyYourIdAfterThisChanges',
			rejectLabel: 'cancel',
			confirmLabel: 'approve',
			yesAction: () => {
				setEditMode(true);
			}
		});
	};

	const handleVerifyUser = async () => {
		if (user.nationalIDVerified) {
			openEditProfileAlert();
			return;
		}
		setIsLoading(true);
		try {
			const response = await axios.post(PROFILE.VERIFY_USER, {
				firstName: watch('firstName'),
				surname: watch('lastName'),
				dateOfBirth: watch('birthdate'),
				identityNo: +watch('idNumber')
			});
			const { status } = response;

			if (status === 200) {
				dispatch(
					setUserDetails({
						...watch(),
						fullName: `${watch('firstName')} ${watch('lastName')}`,
						nationalIDVerified: true,
						dateOfBirth: watch('birthdate'),
						nationalID: +watch('idNumber')
					})
				);
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(`Error while verifying user`, JSON.stringify(error));
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="myProfile" />
				<S.ButtonContainer>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						className="saveButton"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						icon={faFloppyDisk}
						label="save"
						variant="blue"
						disabled={!isDirty || isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>
			<S.StyledForm key="userSettings" formMethods={formMethods} borderless>
				<S.StyledCardWithAvatar className="settingsMaxWidth" fullWidth>
					<S.UserAvatarContainer>
						<S.FlexBox $alignItems="center">
							<Avatar
								defaultImage={
									'https://storage.googleapis.com/tryoto-public/public/assets/images/otonoavatar.png'
								}
								value={watch('avatarPath')}
								setValue={(value) => {
									setValue('avatarPath', value, { shouldValidate: true, shouldDirty: true });
								}}
								maxFileSize={1000000}
								size="large"
							/>
							<S.FlexBox $flexDirection="column" $grid>
								<Typography fontWeight="500" fontSize="1.85rem">
									{user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName}

									{isTrUser && (
										<Icon
											icon={faBadgeCheck}
											color={user.nationalIDVerified ? 'var(--blue)' : 'var(--content-4)'}
											fontSize={'16px'}
										/>
									)}
								</Typography>
								<Typography fontWeight="400" fontSize="1.35rem" color="var(--content-3)">
									{user?.email}
								</Typography>
								{isTrUser && (
									<Typography
										fontWeight="400"
										color={user.nationalIDVerified ? 'var(--blue)' : 'var(--red)'}
										fontSize="1.35rem">
										{user.nationalIDVerified ? 'Verified User' : 'Unverified'}
									</Typography>
								)}
							</S.FlexBox>
						</S.FlexBox>
						{isTrUser && (
							<div>
								<Button
									variant="outline"
									label={user.nationalIDVerified ? 'editProfile' : 'verifyId'}
									icon={user.nationalIDVerified ? faEdit : faBadgeCheck}
									onClick={handleSubmit(handleVerifyUser, (errors) => {
										AppLogger.log(errors);
									})}
									className="add-order-button"
								/>
							</div>
						)}
					</S.UserAvatarContainer>

					{isTrUser && !user.nationalIDVerified ? (
						<Alert showIcon variant="warning" content="mustVerifyAccountDescription" />
					) : (
						<Alert showIcon content="mobileNumberChangeDescription" />
					)}
					<GeneralSettingsContainer>
						<S.FormContainer>
							<FormItem colSpan={defaultColSpan} label="firstName" isLoading={isLoading} required>
								<StringInput name="firstName" disabled={!isEditMode} maxLength="50" />
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="lastName" isLoading={isLoading} required>
								<StringInput name="lastName" disabled={!isEditMode} maxLength="50" />
							</FormItem>

							{isTrUser && (
								<FormItem colSpan={defaultColSpan} label="idNumber" isLoading={isLoading} required>
									<StringInput
										name="idNumber"
										maxlength={11}
										allowOnlyNumbers
										disabled={!isEditMode}
									/>
								</FormItem>
							)}

							{isTrUser && (
								<FormItem colSpan={defaultColSpan} label="dateOfBirth" isLoading={isLoading} required>
									<DateInput
										name="birthdate"
										value={watch('birthdate')}
										setValue={(value) => {
											setValue('birthdate', value, { shouldValidate: true, shouldDirty: true });
										}}
										disabled={!isEditMode}
										maxDate={new Date().toISOString()}
										withoutTime
									/>
								</FormItem>
							)}

							<FormItem colSpan={defaultColSpan} label="email" required isLoading={isLoading}>
								<OTPVerification
									name="email"
									isVerified={user.mailVerified}
									onVerification={(value) => {
										dispatch(
											setUserDetails({
												email: value,
												mailVerified: true
											})
										);
										update({ email: value });
										showWarning({
											message: 'emailChange.loginAgain'
										});
										setTimeout(() => {
											navigate(PATHS.LOGOUT);
										}, 2000);
									}}
									type="email"
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isLoading}>
								<OTPVerification
									name="phone"
									otpType="currentPhone"
									isVerified={user.mobileVerified}
									onVerification={(value) => {
										dispatch(
											setUserDetails({
												phone: value,
												mobileVerified: true,
												...(user.userType === 'companyAdmin' && { companyAdminVerified: true })
											})
										);
										update({ phone: value });
										window.dataLayer = window.dataLayer || [];
										window.dataLayer.push({
											/* eslint-disable camelcase */
											event: 'phone_number_verification',
											first_name: user?.firstName || '',
											last_name: user?.lastName || '',
											phone: value || '',
											email: user?.email || '',
											plan: user?.planName || '',
											country: user?.localization?.label || '',
											userID: user?.ID || ''
											/* eslint-enable camelcase */
										});
									}}
									type="phone"
								/>
							</FormItem>
						</S.FormContainer>
					</GeneralSettingsContainer>
				</S.StyledCardWithAvatar>

				<S.StyledCardWithAvatar className="settingsMaxWidth" fullWidth>
					<S.PreferencesContainer>
						<S.PreferenceActionsContainer>
							<Typography fontWeight="500" fontSize="1.85rem">
								layoutPreference
							</Typography>
							<Typography fontWeight="400" fontSize="1.35rem" noWrap color="var(--content-3)">
								systemPreferencesDescription
							</Typography>
						</S.PreferenceActionsContainer>
						<div>
							<Button
								variant="outline"
								icon={faBrush}
								onClick={() => {
									NiceModal.show(LayoutPreferenceModal);
								}}
								label="configure"
							/>
						</div>
					</S.PreferencesContainer>
				</S.StyledCardWithAvatar>
			</S.StyledForm>
			<FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					className="saveButton"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faFloppyDisk}
					label="save"
					variant="blue"
					disabled={!isDirty || isLoading}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
				/>
			</FormFooter>
		</div>
	);
};

const defaultColSpan = {
	lg: 8,
	xl: 7
};

export default UserSettings;
